/**
 * Página de Login do Sistema MEDICARE
 * Autenticação de usuários
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Heading,
  Link,
  Alert,
  AlertIcon,
  InputGroup,
  InputRightElement,
  IconButton,
  useColorModeValue,
  Flex,
  Image,
  Card,
  CardBody,
  Divider,
} from '@chakra-ui/react';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/ToastContext';

interface LoginFormData {
  email: string;
  senha: string;
}

export const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login } = useAuth();
  const { showSuccess, showError } = useNotifications();
  const navigate = useNavigate();
  
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError('');
    
    try {
      await login(data.email, data.senha);
      showSuccess('Login realizado com sucesso!');
      navigate('/');
    } catch (err: any) {
      const errorMessage = err.response?.data?.mensagem || 'Erro ao fazer login';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg={bgColor}
      px={4}
    >
      <Container maxW="md">
        <Card bg={cardBg} shadow="xl" borderRadius="xl">
          <CardBody p={8}>
            <VStack spacing={6}>
              {/* Logo e título */}
              <VStack spacing={4}>
                <Box
                  w={64}
                  h={64}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Image
                    src="/logo_medicare.png"
                    alt="Logo Medicare"
                    w={64}
                    h={64}
                    objectFit="contain"
                  />
                </Box>
                <VStack spacing={2}>
                  <Heading
                    size="lg"
                    color="medicare.500"
                    textAlign="center"
                  >
                    Sistema MEDICARE
                  </Heading>
                  <Text
                    color={useColorModeValue('gray.600', 'gray.400')}
                    textAlign="center"
                  >
                    Faça login para acessar o sistema
                  </Text>
                </VStack>
              </VStack>

              <Divider />

              {/* Formulário de login */}
              <Box w="full">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <VStack spacing={4}>
                    {error && (
                      <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        {error}
                      </Alert>
                    )}

                    <FormControl isInvalid={!!errors.email}>
                      <FormLabel>E-mail</FormLabel>
                      <Input
                        type="email"
                        placeholder="seu@email.com"
                        {...register('email', {
                          required: 'E-mail é obrigatório',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'E-mail inválido',
                          },
                        })}
                      />
                      {errors.email && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.email.message}
                        </Text>
                      )}
                    </FormControl>

                    <FormControl isInvalid={!!errors.senha}>
                      <FormLabel>Senha</FormLabel>
                      <InputGroup>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Sua senha"
                          {...register('senha', {
                            required: 'Senha é obrigatória',
                            minLength: {
                              value: 6,
                              message: 'Senha deve ter pelo menos 6 caracteres',
                            },
                          })}
                        />
                        <InputRightElement>
                          <IconButton
                            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                            icon={showPassword ? <FiEyeOff /> : <FiEye />}
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowPassword(!showPassword)}
                          />
                        </InputRightElement>
                      </InputGroup>
                      {errors.senha && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.senha.message}
                        </Text>
                      )}
                    </FormControl>

                    <Button
                      type="submit"
                      colorScheme="medicare"
                      size="lg"
                      w="full"
                      isLoading={isLoading}
                      loadingText="Entrando..."
                    >
                      Entrar
                    </Button>
                  </VStack>
                </form>
              </Box>

              {/* Links adicionais */}
              <VStack spacing={2}>
                <Link
                  as={RouterLink}
                  to="/esqueci-senha"
                  color="medicare.500"
                  fontSize="sm"
                >
                  Esqueci minha senha
                </Link>
                <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.400')}>
                  Não tem acesso? Entre em contato com o administrador.
                </Text>
              </VStack>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    </Flex>
  );
};

export default LoginPage;